import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './tufts-button.css.lit';
import { LinkTargetType } from '../../base/outline-link/config';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotNGController } from '../../controllers/slot-ng-controller';

export type ButtonVariant =
  | 'link'
  | 'primary'
  | 'reversed'
  | 'compact'
  | 'player'
  | 'icon';

export type ButtonSize = 'small' | 'medium' | 'large';

/**
 * The tufts Button component
 * @element tufts-button
 * @since 1.0.0
 *
 * @slot - default slot, used for button text.
 * @slot left - slot used for left side icon.
 * @slot right - slot used for right side icon.
 * @todo Convert styles to utilize CSS Variables.
 *
 * @attr icon-type
 * @attr button-url
 * @attr button-label
 * @attr button-target
 * @attr button-variant
 * @attr button-size
 * @attr is-disabled
 */
@customElement('tufts-button')
export class TuftsButton extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  ngSlot = new SlotNGController(this);

  /**
   * The url to use for a link. This will render an anchor element.
   * Do not set this prop if you want to render a button element.
   */

  @property({ type: String, attribute: 'icon-type' })
  iconType = 'arrow-right';

  @property({ type: String, attribute: 'button-url' })
  buttonUrl: string;

  /**
   * ARIA label attribute to pass down to the resulting button or a
   * element. This is required for accessibility if we use a button
   * with an icon only.
   */
  @property({ type: String, attribute: 'button-label' })
  buttonLabel: string;

  /**
   * The target to use for a link, used in conjunction with the url attribute.
   */
  @property({ type: String, attribute: 'button-target' })
  buttonTarget: LinkTargetType;

  /**
   * The button style variant to use.
   */
  @property({ type: String, attribute: 'button-variant', reflect: true })
  buttonVariant: ButtonVariant = 'primary';

  /**
   * The button size to use.
   */
  @property({ type: String, attribute: 'button-size' })
  buttonSize: ButtonSize = 'medium';

  /**
   * Whether the button is disabled. Only applies to
   * implementations not using the url property
   */
  @property({ type: Boolean, attribute: 'is-disabled' })
  isDisabled = false;

  /**
   * A click handler to be passed only to onClick. DO NOT USE @click on this component.
   */
  @property() onClick: () => void;

  /**
   * A keyUp handler to be passed to the onKeyUp. DO NOT USE @keyup on this component.
   */
  @property() onKeyUp: () => void;

  hasLeftIcon: boolean;
  hasRightIcon: boolean;
  /**
   * Check to see if the link is external, pass target="_blank" and rel="external" if so. Returns true if the link is external.
   */
  isURLExternal(url: string): boolean {
    return (
      new URL(url, window.location.href).hostname !== window.location.hostname
    );
  }

  /**
   * TLDR: when a link has the attribute target="_blank", always add ref="noreferrer noopener"
   * https://cheatsheetseries.owasp.org/cheatsheets/HTML5_Security_Cheat_Sheet.html#tabnabbing
   */
  buttonRelRender(): string | undefined {
    return this.isURLExternal(this.buttonUrl)
      ? 'noreferrer noopener'
      : undefined;
  }

  /**
   * You can override target manually, but if not, we'll set it to "_blank" if the link is external.
   */
  buttonTargetRender(): LinkTargetType | undefined {
    if (this.buttonTarget) {
      return this.buttonTarget;
    }
    return this.isURLExternal(this.buttonUrl) ? '_blank' : undefined;
  }

  firstUpdated(): void {
    this.hasLeftIcon = this.ngSlot.exist('left');
    this.hasRightIcon = this.ngSlot.exist('right');
  }

  buttonTemplate(): TemplateResult {
    return this.buttonUrl
      ? html` <a
          class="btn ${this.buttonVariant} ${this.buttonSize} ${this.iconType}"
          href=${this.buttonUrl}
          target="${ifDefined(this.buttonTargetRender())}"
          rel="${ifDefined(this.buttonRelRender())}"
          aria-label="${ifDefined(this.buttonLabel)}"
          aria-disabled="${ifDefined(this.isDisabled)}"
        >
          ${this.iconTemplate(this.hasLeftIcon, 'left')}
          ${this.ngSlot.renderInShadow()}
          ${this.iconTemplate(this.hasRightIcon, 'right')}
        </a>`
      : html`<button
          class="btn ${this.buttonVariant} ${this.buttonSize} ${this.iconType}"
          aria-label="${ifDefined(this.buttonLabel)}"
          aria-disabled="${ifDefined(this.isDisabled)}"
          .onclick="${this.onClick}"
          .onkeyup="${this.onKeyUp}"
        >
          ${this.iconTemplate(this.hasLeftIcon, 'left')}
          ${this.ngSlot.renderInShadow()}
          ${this.iconTemplate(this.hasRightIcon, 'right')}
        </button> `;
  }

  linkInSlotTemplate(): TemplateResult {
    const link = this.querySelector('a');
    if (!link) {
      return html`<div
        class="btn ${this.buttonVariant} ${this.buttonSize} ${this.iconType}"
        aria-disabled="true"
      >
        ${this.ngSlot.renderInShadow()}
      </div>`;
    } else {
      const buttonVariantClasses = this.buttonVariant;
      const classesArray = [
        ...buttonVariantClasses.split(' '),
        'btn',
        this.buttonSize,
        this.iconType,
      ];
      // Remove all empty strings from classes array, to prevent errors in `link.classList.add` below
      const filteredClassesArray = classesArray.filter(item => item !== '');
      link.classList.add(...filteredClassesArray);
      return html`
        ${this.iconTemplate(this.hasLeftIcon, 'left')}
        ${this.ngSlot.renderInShadow()}
        ${this.iconTemplate(this.hasRightIcon, 'right')}
      `;
    }
  }

  /**
   * Component render function
   * @returns TemplateResult
   * @todo This should utilize `outline-link` component.
   */
  render(): TemplateResult {
    return this.buttonLabel
      ? // If button-label provided, expect slot with text only
        this.buttonTemplate()
      : // If button-label is not provided, expect slot with a full link markup
        this.linkInSlotTemplate();
  }

  /**
   * Icon template
   *
   * @param exists - Whether the icon slot exists.
   * @param slot - The slot name to use.
   * @returns - The icon template.
   */
  iconTemplate(exists: boolean, slotName: string): TemplateResult | null {
    if (!exists) return null;
    return html`${this.ngSlot.renderInShadow(slotName)}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-button': TuftsButton;
  }
}
